import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, fmtUsd } from "@/lib/api";
import { announceAlert } from "@/lib/attention";

const PRICE_KINDS = ["price_above", "price_below", "range_enter", "range_exit", "sr_break"];
const hitFor = (a, p) => a.kind === "price_above" ? p >= a.threshold : a.kind === "price_below" ? p <= a.threshold
  : a.kind === "sr_break" ? (a.side === "resistance" ? p >= a.threshold : p <= a.threshold)
  : a.kind === "range_enter" ? (p >= a.low && p <= a.high) : (p < a.low || p > a.high);

// Watches the user's untriggered price alerts (incl. bracket legs) for one coin and fires them from the live feed.
export const useCoinLiveAlerts = (coinId, live) => {
  const [alerts, setAlerts] = useState([]);
  const firing = useRef(new Set());

  const reload = useCallback(() => {
    api.get("/alerts")
      .then((r) => setAlerts((r.data || []).filter((a) => a.coin_id === coinId && a.status === "active" && PRICE_KINDS.includes(a.kind))))
      .catch((e) => console.warn("[apex] request failed:", e?.message || e));
  }, [coinId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const p = live.price;
    if (p == null || !alerts.length) return;
    alerts.forEach((a) => {
      if (!hitFor(a, p) || firing.current.has(a.id)) return;
      firing.current.add(a.id);
      api.post(`/alerts/${a.id}/trigger`, { price: p, source: live.source || "live" })
        .then((r) => {
          if (r.data.fired) {
            const sym = (a.symbol || "").toUpperCase();
            const title = a.role === "stop" ? `${sym} stop hit at ${fmtUsd(a.threshold)}`
              : a.role === "target" ? `${sym} target hit at ${fmtUsd(a.threshold)}`
              : `${sym}: ${a.condition || a.kind}`;
            toast.success(`${title} — alert fired`, { duration: 8000 });
            announceAlert(title, `Live price ${fmtUsd(p)} — review the chart. No auto-trade was executed.`);
          }
          window.dispatchEvent(new Event("apx:notifications-changed"));
          reload();
        })
        .catch(() => { firing.current.delete(a.id); reload(); });
    });
  }, [live.price, alerts, live.source, reload]);

  return { alerts, reload };
};
